// pages/ReservePage.jsx - Yeni rezervasyon sistemi

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

// Yeni componentler
import RoomSelector from '../components/reserve/RoomSelector';
import WeeklyCalendar from '../components/reserve/WeeklyCalendar';
import TimeSlotGrid from '../components/reserve/TimeSlotGrid';
import ReservationTypeSelector from '../components/reserve/ReservationTypeSelector';
import PriceSummary from '../components/reserve/PriceSummary';
import AgreementCheckboxes from '../components/reserve/AgreementCheckboxes';
import IBANDisplay from '../components/reserve/IBANDisplay';
import ReceiptUpload from '../components/receipt/ReceiptUpload';
import useAuth from '../hooks/useAuth';

// Yeni API
import { getRooms, getPricing, getSystemSettings, createBooking, formatDate } from '../utils/newApi';

const ReservePage = () => {
  const { user } = useAuth();

  // Step tracking
  const [currentStep, setCurrentStep] = useState(1); // 1: Oda, 2: Tarih/Saat, 3: Detaylar, 4: Ödeme/Onay
  
  // Room selection
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [pricingRules, setPricingRules] = useState({});
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  
  // Date/Time selection
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Reservation details
  const [reservationType, setReservationType] = useState('individual');
  const [numberOfPeople, setNumberOfPeople] = useState(1);
  const [duration, setDuration] = useState(1);
  
  // Agreements
  const [capacityAgreed, setCapacityAgreed] = useState(false);
  const [receiptAgreed, setReceiptAgreed] = useState(false);
  const [cancelPolicyAgreed, setCancelPolicyAgreed] = useState(false);
  
  // Payment info
  const [systemSettings, setSystemSettings] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  
  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [createdPin, setCreatedPin] = useState(null);

  const navigate = useNavigate();

  // Load rooms on mount
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoadingRooms(true);
        const roomsData = await getRooms();
        console.log('🔍 Loaded rooms:', roomsData.map(r => ({ 
          roomId: r.roomId, 
          name: r.shortName || r.name, 
          isActive: r.isActive 
        })));
        setRooms(roomsData);
        
        // Pricing'i seed et ve preview için hazırla
        const pricingData = await getPricing();
        console.log('💰 Loaded pricing:', pricingData);
        setPricingRules(pricingData || {});
      } catch (error) {
        console.error('Error fetching rooms:', error);
        setErrorMessage('Odalar yüklenirken hata oluştu.');
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
  }, []);

  // Load system settings for IBAN
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSystemSettings();
        setSystemSettings(settings);
      } catch (error) {
        console.error('Error fetching system settings:', error);
      }
    };

    fetchSettings();
  }, []);

  // Reset time slot when date changes
  useEffect(() => {
    setSelectedSlot(null);
  }, [selectedDate]);

  // Validation for each step
  const canProceedToStep2 = selectedRoomId !== null;
  const canProceedToStep3 = selectedDate !== null && selectedSlot !== null && duration >= 1;
  const canProceedToStep4 = reservationType && (reservationType === 'individual' || numberOfPeople >= 2);
  const canSubmit = capacityAgreed && receiptAgreed && cancelPolicyAgreed;

  const handleSubmit = async () => {
    console.log('Agreement status:', {
      capacityAgreed,
      receiptAgreed,
      cancelPolicyAgreed,
      canSubmit: capacityAgreed && receiptAgreed && cancelPolicyAgreed,
      receiptUrl: receiptUrl
    });

    if (!canSubmit) {
      alert('Lütfen tüm taahhütnameleri onaylayınız.');
      return;
    }

    if (!receiptUrl) {
      alert('Lütfen dekont yükleyiniz.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    setCreatedPin(null);

    try {
      // Calculate end time based on duration
      const [startHour, startMinute] = selectedSlot.split(':').map(Number);
      const endHour = startHour + duration;
      const endTime = `${endHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;

      const result = await createBooking({
        date: formatDate(selectedDate),
        startTime: selectedSlot,
        endTime: endTime,
        roomId: selectedRoomId,
        reservationType: reservationType,
        numberOfPeople: numberOfPeople,
        capacityAgreementAccepted: capacityAgreed,
        receiptAgreementAccepted: receiptAgreed,
        cancelPolicyAccepted: cancelPolicyAgreed,
        receiptUrl: receiptUrl
      });

      if (result.success) {
        setCreatedPin(result.pin);
        setSuccessMessage(result.message || 'Rezervasyon başarıyla oluşturuldu!');
        
        // Başarı sayfasına yönlendir
        navigate('/reservation-success', {
          state: {
            pin: result.pin,
            message: result.message || 'Rezervasyon başarıyla oluşturuldu!',
            reservationDetails: {
              room: rooms.find(r => r.roomId === selectedRoomId)?.shortName || rooms.find(r => r.roomId === selectedRoomId)?.name,
              date: formatDate(selectedDate),
              time: `${selectedSlot} - ${endTime}`,
              duration: duration
            }
          }
        });
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      setErrorMessage(error.message || 'Rezervasyon oluşturulurken bir hata oluştu.');
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8 fade-in">
          <svg className="w-8 h-8 text-blue-300 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h1 className="text-3xl font-bold text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-indigo-200">
            Müzik Odası Rezervasyonu
          </h1>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[
              { num: 1, title: 'Oda Seçimi' },
              { num: 2, title: 'Tarih, Saat ve Tip' },
              { num: 3, title: 'Ödeme ve Onay' }
            ].map((step, idx) => (
              <div key={step.num} className="flex-1 flex items-center">
                <div className="flex flex-col items-center flex-1">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-bold
                    ${currentStep >= step.num 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 text-gray-400'
                    }
                  `}>
                    {step.num}
                  </div>
                  <div className={`
                    text-xs mt-2
                    ${currentStep >= step.num ? 'text-blue-300' : 'text-gray-500'}
                  `}>
                    {step.title}
                  </div>
                </div>
                {idx < 2 && (
                  <div className={`
                    h-1 flex-1 mx-2
                    ${currentStep > step.num ? 'bg-blue-600' : 'bg-gray-700'}
                  `} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-900 bg-opacity-80 border border-red-400 text-red-100 px-6 py-4 rounded-lg mb-6 shadow-lg flex items-center slide-up">
            <svg className="w-6 h-6 text-red-300 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {errorMessage}
          </div>
        )}

        {/* Step 1: Room Selection */}
        {currentStep === 1 && (
          <Card className="fade-in">
            <h2 className="text-2xl font-bold text-blue-100 mb-6">1. Oda Seçimi</h2>
            <RoomSelector
              rooms={rooms}
              selectedRoomId={selectedRoomId}
              onSelectRoom={(roomId) => {
                setSelectedRoomId(roomId);
                const room = rooms.find(r => r.roomId === roomId);
                setSelectedRoom(room);
              }}
              loading={loadingRooms}
            />
            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setCurrentStep(2)}
                disabled={!canProceedToStep2}
                className="px-8 py-3"
              >
                Devam Et →
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Date, Time and Type */}
        {currentStep === 2 && (
          <div className="space-y-6 fade-in">
            <Card>
              <h2 className="text-2xl font-bold text-blue-100 mb-6">2. Tarih, Saat ve Rezervasyon Tipi</h2>
              
              {/* Rezervasyon Tipi */}
              <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h3 className="text-lg font-semibold text-blue-200 mb-3">Rezervasyon Tipi</h3>
                <ReservationTypeSelector
                  reservationType={reservationType}
                  onChangeType={setReservationType}
                  numberOfPeople={numberOfPeople}
                  onChangeNumberOfPeople={setNumberOfPeople}
                />
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-200 mb-3">Tarih Seçin</h3>
                <WeeklyCalendar
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-200 mb-3">Saat ve Süre Seçin</h3>
                <TimeSlotGrid
                  roomId={selectedRoomId}
                  date={selectedDate}
                  selectedSlot={selectedSlot}
                  onSelectSlot={setSelectedSlot}
                  duration={duration}
                />
              </div>
              <div className="mt-6">
                <PriceSummary
                  reservationType={reservationType}
                  startTime={selectedSlot}
                  endTime={selectedSlot ? (() => {
                    const [h, m] = selectedSlot.split(':').map(Number);
                    return `${(h + duration).toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                  })() : null}
                  duration={duration}
                  userEmail={user?.email || ''}
                  pricingRules={pricingRules}
                  onChangeDuration={setDuration}
                />
              </div>
            </Card>
            <div className="flex justify-between">
              <Button
                onClick={() => setCurrentStep(1)}
                className="px-8 py-3 bg-gray-600 hover:bg-gray-500"
              >
                ← Geri
              </Button>
              <Button
                onClick={() => {
                  if (!canProceedToStep3) {
                    alert('Lütfen tarih ve saat seçiniz.');
                    return;
                  }
                  if (!canProceedToStep4) {
                    alert('Lütfen rezervasyon tipini ve kişi sayısını belirleyiniz.');
                    return;
                  }
                  setCurrentStep(3);
                }}
                disabled={!canProceedToStep3 || !canProceedToStep4}
                className="px-8 py-3"
              >
                Devam Et →
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Payment and Agreements (eski Step 4) */}
        {currentStep === 3 && (
          <div className="space-y-6 fade-in">
            {/* Seçim Özeti - Sticky */}
            <div className="sticky top-20 z-10 bg-gray-900/95 backdrop-blur-sm p-4 rounded-lg border border-blue-600 shadow-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">Oda:</span>
                  <span className="ml-2 text-blue-300 font-semibold">
                    {selectedRoom?.shortName || selectedRoom?.name || '-'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Tarih:</span>
                  <span className="ml-2 text-blue-300 font-semibold">
                    {selectedDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Saat:</span>
                  <span className="ml-2 text-blue-300 font-semibold">
                    {selectedSlot} ({duration}s)
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Tip:</span>
                  <span className="ml-2 text-blue-300 font-semibold">
                    {reservationType === 'individual' ? 'Bireysel' : `Grup (${numberOfPeople})`}
                  </span>
                </div>
              </div>
            </div>

            <Card>
              <h2 className="text-2xl font-bold text-blue-100 mb-6">3. Ödeme ve Taahhütnameler</h2>
              
              {/* Final Summary */}
              <div className="mb-6 p-5 bg-blue-900/30 rounded-lg border border-blue-600">
                <h3 className="text-lg font-semibold text-blue-200 mb-4">Rezervasyon Özeti</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-300">Oda:</span>
                    <span className="ml-2 text-gray-100 font-medium">
                      {selectedRoom?.shortName || selectedRoom?.name || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-300">Tarih:</span>
                    <span className="ml-2 text-gray-100 font-medium">
                      {formatDate(selectedDate)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-300">Saat:</span>
                    <span className="ml-2 text-gray-100 font-medium">
                      {selectedSlot} - {(() => {
                        const [h, m] = selectedSlot.split(':').map(Number);
                        return `${(h + duration).toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-300">Süre:</span>
                    <span className="ml-2 text-gray-100 font-medium">{duration} saat</span>
                  </div>
                  <div>
                    <span className="text-gray-300">Tip:</span>
                    <span className="ml-2 text-gray-100 font-medium">
                      {reservationType === 'individual' ? 'Bireysel' : 'Grup'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-300">Kişi Sayısı:</span>
                    <span className="ml-2 text-gray-100 font-medium">{numberOfPeople}</span>
                  </div>
                </div>
              </div>

              {/* IBAN Display */}
              {systemSettings?.iban && (
                <div className="mb-6">
                  <IBANDisplay iban={systemSettings.iban} />
                </div>
              )}

              {/* Dekont Yükleme */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-200 mb-3">Dekont Yükleme</h3>
                <p className="text-sm text-gray-300 mb-4">
                  Ödeme dekontunuzu yükleyin. Admin onayından sonra rezervasyonunuz aktif olacaktır.
                </p>
                <ReceiptUpload 
                  onUploadComplete={(url) => setReceiptUrl(url)}
                  disabled={isSubmitting}
                />
              </div>

              {/* Agreements */}
              <div className="mb-6">
                <AgreementCheckboxes
                  capacityAgreed={capacityAgreed}
                  receiptAgreed={receiptAgreed}
                  cancelPolicyAgreed={cancelPolicyAgreed}
                  onCapacityAgree={setCapacityAgreed}
                  onReceiptAgree={setReceiptAgreed}
                  onCancelPolicyAgree={setCancelPolicyAgreed}
                />
              </div>
            </Card>

            <div className="flex justify-between">
              <Button
                onClick={() => setCurrentStep(2)}
                className="px-8 py-3 bg-gray-600 hover:bg-gray-500"
              >
                ← Geri
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
                className="px-8 py-3"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <div className="loader mr-3"></div>
                    Rezervasyon Oluşturuluyor...
                  </span>
                ) : 'Rezervasyonu Tamamla'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReservePage;